import { useState, useCallback, useRef } from 'react';

/**
 * Copy text (and optionally HTML) to the clipboard.
 *
 * When `html` is provided, Word / Google Docs / Outlook will paste the rich
 * version while plain-text editors fall back to the `text` payload.
 */
export function useCopyToClipboard(resetDelay = 2000) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const copy = useCallback(async (text: string, html?: string) => {
    try {
      if (html && typeof ClipboardItem !== 'undefined') {
        const item = new ClipboardItem({
          'text/plain': new Blob([text], { type: 'text/plain' }),
          'text/html': new Blob([html], { type: 'text/html' }),
        });
        await navigator.clipboard.write([item]);
      } else {
        await navigator.clipboard.writeText(text);
      }
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), resetDelay);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [resetDelay]);

  return { copied, copy };
}
